import Tree from './Tree';
import treeSelectUtils from './treeSelectUtils';
import type { TreeSelectItemBase } from './types';

type Props<T extends {}> = {
  tree: TreeSelectItemBase<T>[];
  values: FilterKeyValue[];
  onChange: (values: FilterKeyValue[]) => void;
  parser: (item: TreeSelectItemBase<T>) => string;
};

export const CascadingTree = <T extends {}>(props: Props<T>) => {
  const { tree, values, onChange, parser } = props;
  const onCheck = ({
    value,
    parentSelected,
  }: {
    value: StringNumberValue;
    parentSelected?: boolean;
  }) => {
    const node = treeSelectUtils.findNode(tree, value);
    if (!node) {
      console.error('Node not found', value);
      return;
    }

    const isExist = values.some((item) => item.filterKey === value);
    if (isExist) {
      // 이미 존재하는 값인 경우 제거
      onChange(values.filter((item) => item.filterKey !== value));
      return;
    }

    if (parentSelected) {
      let newValues: FilterKeyValue[] = [...values];

      // 직계 부모의 siblings 추가
      // - 최상위 부모 레벨 siblings는 제외
      // - 직계 부모 항목 자신은 제외
      const parents = treeSelectUtils.getAllParentNode(tree, value);
      const topSelectedParent = parents.find((parent) =>
        newValues.some((item) => item.filterKey === parent.alias),
      );

      newValues = newValues.filter(
        (item) => !parents.some((parent) => parent.alias === item.filterKey),
      );

      for (const parent of parents) {
        if (parent.alias === topSelectedParent?.alias) continue;
        const siblings = treeSelectUtils
          .getSiblings(tree, parent.alias)
          .filter((sibling) => sibling.alias !== parent.alias);
        newValues.push(
          ...siblings.map((sibling) => {
            return {
              filterKey: sibling.alias,
              filterName: parser(sibling),
            };
          }),
        );
      }

      // Target을 제외한 siblings 추가
      // 자신을 제외한 형제 노드 선택
      const siblingsWithoutSelf = treeSelectUtils
        .getSiblings(tree, value)
        .filter((sibling) => sibling.alias !== value);

      newValues.push(
        ...siblingsWithoutSelf.map((sibling) => {
          return {
            filterKey: sibling.alias,
            filterName: parser(sibling),
          };
        }),
      );

      onChange(newValues);
      return;
    }

    // 부모가 선택되지 않은 경우
    if (!parentSelected) {
      // 이미 선택된 자식 노드들이 있는 경우 자식 노드들을 삭제하고 자신을 추가
      const allChildAliases = treeSelectUtils.getAllChildAliases(
        node.childNodes || [],
      );
      const newValues = props.values.filter(
        (item) =>
          !allChildAliases.some((childAlias) => childAlias === item.filterKey),
      );

      const result = [
        ...newValues,
        {
          filterKey: value,
          filterName: parser(node),
        },
      ];

      props.onChange(result);
      return;
    }

    throw new Error('Unexpected error');
  };

  return (
    <Tree
      mode="multiple"
      isCascading
      dataList={tree}
      selectedList={values.map((item) => item.filterKey)}
      onCheck={onCheck}
      loading={false}
    />
  );
};
